/* ============================================================
   SLICK STORE — SHARED WISHLIST ENGINE
   File: assets/ss-wishlist.js
   ============================================================
   Single source of truth for ALL wishlist behavior:
     - localStorage read/write/normalize
     - Add/remove toggle (window.toggleSlickWishlist)
     - Button visual sync (window.syncSlickWishlistButtons)
     - Header badge count update
     - Dynamic content observer (related products, etc)
     - Cross-tab sync via storage event
 
   Loaded from layout/theme.liquid so EVERY page has it.
   Reads thresholds from window.SS_WISHLIST_CONFIG which
   is populated by snippets/ss-wishlist-config.liquid.
 
   Replaces inline duplicate scripts in:
     - snippets/card-product.liquid
     - sections/related-products.liquid
     - sections/header.liquid
     - snippets/ss-product-info.liquid (partially - product
       page keeps its variant-aware ssWishlist wrapper)
   ============================================================ */
 
(function () {
  'use strict';
 
  /* ------------------------------------------------------------
     CONFIG — read from window.SS_WISHLIST_CONFIG (defensive)
     ------------------------------------------------------------
     The Liquid bridge in ss-wishlist-config.liquid sets these
     values. If anything goes wrong loading the bridge, we use
     sensible defaults so the page NEVER breaks.
  ------------------------------------------------------------ */
  var CONFIG = window.SS_WISHLIST_CONFIG || {};
  var STORAGE_KEY = 'shopify_wishlist';
 
  /* ------------------------------------------------------------
     wlKey(pid, vid)
     ------------------------------------------------------------
     Builds the unique identity for one saved wishlist entry.
     Format: "productId:variantId" — variantId falls back to
     productId when no variant info is present.
     Two items with the same productId but different variants
     are kept as separate entries.
  ------------------------------------------------------------ */
  function wlKey(pid, vid) {
    return String(pid) + ':' + String(vid || pid);
  }
 
  /* ------------------------------------------------------------
     normalizeWishlist(list)
     ------------------------------------------------------------
     The CRITICAL function. Cleans saved wishlist data:
       1. Ensures every item has the same shape (id, handle,
          variantId, title, image, url, price, discount, etc).
       2. Carries forward ALL fields — including discount,
          comparePrice, vendor — so the wishlist page can
          render badges and compare-at prices correctly.
       3. Removes duplicates (same productId + variantId).
       4. Accepts BOTH naming conventions:
          - discount        OR discountPct
          - comparePrice    OR compareAtPrice
          ...so it doesn't matter which file wrote the entry.
 
     Old bug: previous normalize functions in card-product
     dropped the discount and comparePrice fields. This
     version preserves them.
  ------------------------------------------------------------ */
  function normalizeWishlist(list) {
    var seen = {};
    var clean = [];
    if (!Array.isArray(list)) return clean;
 
    list.forEach(function (item) {
      if (!item || !item.id) return;
 
      // Pick whichever name was used when saving
      var comparePrice    = item.comparePrice    || item.compareAtPrice    || '';
      var comparePriceRaw = item.comparePriceRaw || item.compareAtPriceRaw || '';
      var discount        = item.discount        || item.discountPct       || '';
 
      var next = {
        id:                String(item.id),
        handle:            item.handle || '',
        variantId:         String(item.variantId || item.id),
        variantTitle:      item.variantTitle || '',
        title:             item.title || 'Product',
        image:             item.image || '',
        url:               item.url || '#',
        price:             item.price || '',
        priceRaw:          item.priceRaw || '',
        vendor:            item.vendor || '',
        // Save BOTH naming styles for forward compatibility
        comparePrice:      comparePrice,
        comparePriceRaw:   comparePriceRaw,
        compareAtPrice:    comparePrice,
        compareAtPriceRaw: comparePriceRaw,
        discount:          discount,
        discountPct:       discount,
        available:         item.available !== false && item.available !== 'false',
        savedAt:           item.savedAt || Date.now()
      };
 
      var key = wlKey(next.id, next.variantId);
      if (!seen[key]) {
        seen[key] = true;
        clean.push(next);
      }
    });
 
    return clean;
  }
 
  /* ------------------------------------------------------------
     readWishlist() and writeWishlist(list)
     ------------------------------------------------------------
     Wrappers around localStorage. readWishlist also normalizes
     and saves back the cleaned version (so duplicates and stale
     entries get cleaned automatically).
  ------------------------------------------------------------ */
  function readWishlist() {
    var raw = [];
    try {
      raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
      raw = [];
    }
    var clean = normalizeWishlist(raw);
    // Only re-save if normalization actually changed something
    if (JSON.stringify(raw) !== JSON.stringify(clean)) {
      writeWishlist(clean);
    }
    return clean;
  }
 
  function writeWishlist(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // localStorage unavailable (private mode, full quota, etc)
      // We can't save, but the in-memory state still works for
      // the current page load.
    }
  }
 
  /* ------------------------------------------------------------
     toggleSlickWishlist(btn, event)
     ------------------------------------------------------------
     Main entry point. Called by every wishlist button via:
       onclick="return window.toggleSlickWishlist(this, event);"
 
     Reads all data-* attributes off the button, decides whether
     to add or remove from the wishlist, persists the change,
     and fires the wishlist-updated event so other pages/tabs
     can sync.
  ------------------------------------------------------------ */
  function toggleSlickWishlist(btn, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    }
    if (!btn) return false;
 
    // Pull all metadata off the button
    var pid             = btn.getAttribute('data-product-id');
    if (!pid) return false;
 
    var handle          = btn.getAttribute('data-product-handle') || '';
    var variantId       = btn.getAttribute('data-variant-id') || pid;
    var variantTitle    = btn.getAttribute('data-variant-title') || '';
    var title           = btn.getAttribute('data-product-title') || 'Product';
    var image           = btn.getAttribute('data-product-image') || '';
    var url             = btn.getAttribute('data-product-url') || '#';
    var price           = btn.getAttribute('data-product-price') || '';
    var vendor          = btn.getAttribute('data-product-vendor') || '';
    var priceRaw        = btn.getAttribute('data-product-price-raw') || '';
    var comparePrice    = btn.getAttribute('data-product-compare-price') || '';
    var comparePriceRaw = btn.getAttribute('data-product-compare-price-raw') || '';
    var discount        = btn.getAttribute('data-product-discount') || '';
    var available       = btn.getAttribute('data-product-available') !== 'false';
 
    var list  = readWishlist();
    var key   = wlKey(pid, variantId);
    var idx   = list.findIndex(function (i) {
                  return wlKey(i.id, i.variantId) === key;
                });
 
    if (idx === -1) {
      // Not in wishlist — add it
      list.push({
        id:                pid,
        handle:            handle,
        variantId:         variantId,
        variantTitle:      variantTitle,
        title:             title,
        image:             image,
        url:               url,
        price:             price,
        vendor:            vendor,
        priceRaw:          priceRaw,
        comparePrice:      comparePrice,
        comparePriceRaw:   comparePriceRaw,
        compareAtPrice:    comparePrice,
        compareAtPriceRaw: comparePriceRaw,
        discount:          discount,
        discountPct:       discount,
        available:         available,
        savedAt:           Date.now()
      });
      setButtonState(btn, true);
    } else {
      // Already in wishlist — remove it
      list.splice(idx, 1);
      setButtonState(btn, false);
    }
 
    writeWishlist(list);
 
    // Notify the rest of the page / other tabs
    window.dispatchEvent(new Event('storage'));
    document.dispatchEvent(new CustomEvent('wishlist-updated', {
      detail: {
        id:        pid,
        variantId: variantId,
        added:     idx === -1
      }
    }));
 
    // Sync all other buttons on the page in case the same
    // variant appears multiple times (e.g. grid + slick-featured)
    syncSlickWishlistButtons();
 
    return false;
  }
 
  /* ------------------------------------------------------------
     setButtonState(btn, wished)
     ------------------------------------------------------------
     Toggles the visual + accessibility state of one button.
  ------------------------------------------------------------ */
  function setButtonState(btn, wished) {
    btn.classList.toggle('is-wished', wished);
    btn.setAttribute('aria-pressed', wished ? 'true' : 'false');
    btn.setAttribute('aria-label', wished ? 'Remove from wishlist' : 'Add to wishlist');
  }
 
  /* ------------------------------------------------------------
     syncSlickWishlistButtons()
     ------------------------------------------------------------
     Reads the current wishlist from storage, then loops over
     every wishlist button on the page and sets its state to
     match. Run on:
       - page load
       - after any toggle
       - wishlist-updated event
       - storage event (cross-tab sync)
       - DOM mutations (related products loading)
  ------------------------------------------------------------ */
  function syncSlickWishlistButtons() {
    var list = readWishlist();
    var keys = list.map(function (i) { return wlKey(i.id, i.variantId); });
 
    var selector = '.card__wishlist-btn[data-product-id], '
                 + '.product__wishlist-btn[data-product-id], '
                 + '.spc-wishlist-btn[data-product-id]';
 
    document.querySelectorAll(selector).forEach(function (btn) {
      var key = wlKey(
        btn.getAttribute('data-product-id'),
        btn.getAttribute('data-variant-id')
      );
      setButtonState(btn, keys.indexOf(key) > -1);
    });
  }
 
  /* ------------------------------------------------------------
     updateWishlistHeader()
     ------------------------------------------------------------
     Updates the header count badge + active class on the
     wishlist icon. Used to live inline in header.liquid.
  ------------------------------------------------------------ */
  function updateWishlistHeader() {
    var count = readWishlist().length;
 
    var badge   = document.getElementById('wishlist-count-badge');
    var icon    = document.querySelector('.header-wishlist-icon');
    var srText  = document.getElementById('wishlist-count-text');
 
    if (badge) {
      badge.textContent   = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    }
 
    if (icon) {
      icon.classList.toggle('has-items', count > 0);
    }
 
    if (srText) {
      srText.textContent = count > 0
        ? 'Wishlist, ' + count + ' items'
        : 'Wishlist';
    }
  }
 
  /* ------------------------------------------------------------
     refreshAll() — convenience batch refresh
  ------------------------------------------------------------ */
  function refreshAll() {
    syncSlickWishlistButtons();
    updateWishlistHeader();
  }
 
  /* ------------------------------------------------------------
     Public API exposed on window
     ------------------------------------------------------------
     We export these names because existing HTML uses:
       onclick="return window.toggleSlickWishlist(this, event);"
     and external scripts may call window.syncSlickWishlistButtons().
  ------------------------------------------------------------ */
  window.toggleSlickWishlist        = toggleSlickWishlist;
  window.syncSlickWishlistButtons   = syncSlickWishlistButtons;
  window.updateWishlistHeader       = updateWishlistHeader;
  window.SlickWishlist = {
    read:      readWishlist,
    write:     writeWishlist,
    normalize: normalizeWishlist,
    key:       wlKey,
    refresh:   refreshAll,
    storageKey: STORAGE_KEY
  };
 
  /* ------------------------------------------------------------
     Boot — wire up all the events
     ------------------------------------------------------------ */
  function boot() {
    refreshAll();
 
    // Cross-tab sync: another browser tab updated localStorage
    window.addEventListener('storage', function (e) {
      if (!e.key || e.key === STORAGE_KEY) refreshAll();
    });
 
    // Internal event after any toggle
    document.addEventListener('wishlist-updated', refreshAll);
 
    // Watch for new wishlist buttons appearing dynamically
    // (related-products section loads after page render).
    // Debounced so we don't run on every DOM change.
    var debounceTimer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        syncSlickWishlistButtons();
      }, 200);
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree:   true
    });
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
